import React, { useEffect, useState, useMemo } from 'react';
import { InstanceConfig } from '../../core/platform/types';
import { CreateInstanceForm } from './CreateInstanceForm';
import { EditInstanceForm } from './EditInstanceForm';
import { ScreenshotModal } from './ScreenshotModal';
import { authService } from '../services/auth';
import { 
  IconPlus, 
  IconRefresh, 
  IconPlay, 
  IconStop, 
  IconEdit, 
  IconTrash, 
  IconImage, 
  IconCpu, 
  IconSearch, 
  IconChevronDown, 
  IconChevronUp, 
  IconAlert,
  IconStarFilled,
  IconTerminal,
  IconCamera
} from './Icons';

const API_BASE_URL = '';

export const InstanceList: React.FC = () => {
  const [instances, setInstances] = useState<InstanceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingInstance, setEditingInstance] = useState<InstanceConfig | null>(null);
  const [screenshotInstance, setScreenshotInstance] = useState<InstanceConfig | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedInstanceId, setExpandedInstanceId] = useState<string | null>(null);
  
  // Поиск и фильтрация
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'running' | 'stopped'>('all');

  useEffect(() => {
    fetchInstances();
    const interval = setInterval(fetchInstances, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchInstances = async () => {
    try {
      setError(null);
      const token = authService.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/instances`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Ошибка сервера: ${response.status}`);
      }
      
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Некорректный формат данных от сервера');
      }

      setInstances(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      console.error('failed to fetch instances:', err);
      setError(`Не удалось загрузить список ботов: ${errorMessage}`);
      setInstances([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInstance = async (data: any) => {
    try {
      setError(null);
      const token = authService.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/instances`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Ошибка при создании бота');
      }

      await fetchInstances();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(`Ошибка создания: ${errorMessage}`);
      throw err;
    }
  };

  const handleStart = async (id: string) => {
    try {
      setActionLoading(id);
      setError(null);
      const token = authService.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/instances/${id}/start`, { 
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Ошибка при запуске инстанса');
      }

      await fetchInstances();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(`Ошибка запуска бота [${id}]: ${errorMessage}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStop = async (id: string) => {
    try {
      setActionLoading(id);
      setError(null);
      const token = authService.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/instances/${id}/stop`, { 
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Ошибка при остановке инстанса');
      }

      await fetchInstances();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(`Ошибка остановки бота [${id}]: ${errorMessage}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdate = async (data: any) => {
    try {
      setError(null);
      const token = authService.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/instances/${data.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Ошибка при обновлении бота');
      }

      await fetchInstances();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(`Ошибка обновления: ${errorMessage}`);
      throw err;
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Подтвердите удаление бота "${name || id}"? Действие необратимо.`)) {
      return;
    }

    try {
      setError(null);
      const token = authService.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/instances/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка удаления');
      }

      await fetchInstances();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(`Ошибка удаления: ${errorMessage}`);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedInstanceId(prev => prev === id ? null : id);
  };

  const runningCount = instances.filter(i => i.status === 'running').length;
  const stoppedCount = instances.filter(i => i.status === 'stopped').length;
  const aiErrorCount = instances.filter(i => Boolean(i.lastError)).length;

  const filteredInstances = useMemo(() => {
    return instances.filter(inst => {
      // Фильтр по статусу
      if (statusFilter !== 'all' && inst.status !== statusFilter) {
        return false;
      }
      // Фильтр по поиску
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const idMatch = inst.id.toLowerCase().includes(q);
        const nameMatch = (inst.config?.name || '').toLowerCase().includes(q);
        const tgMatch = (inst.config?.tgProfile || '').toLowerCase().includes(q);
        const countryMatch = (inst.config?.country || '').toLowerCase().includes(q);
        return idMatch || nameMatch || tgMatch || countryMatch;
      }
      return true;
    });
  }, [instances, statusFilter, searchQuery]);

  return (
    <div className="instances-container">
      {/* Верхняя статус-панель ключевых метрик */}
      <div className="dashboard-stats-bar">
        <div className="stats-group">
          <div className="stat-card">
            <span className="stat-label">ВСЕГО ИНСТАНСОВ</span>
            <span className="stat-val">{instances.length}</span>
          </div>
          <div className="stat-card stat-success">
            <span className="stat-label">АКТИВНЫХ БОТОВ</span>
            <span className="stat-val">{runningCount}</span>
          </div>
          <div className="stat-card stat-muted">
            <span className="stat-label">ОСТАНОВЛЕНО</span>
            <span className="stat-val">{stoppedCount}</span>
          </div>
          {aiErrorCount > 0 && (
            <div className="stat-card stat-error" title="Боты с ошибками генерации нейросети">
              <span className="stat-label">СБОИ AI</span>
              <span className="stat-val">{aiErrorCount}</span>
            </div>
          )}
        </div>

        <div className="stats-actions">
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            <IconPlus size={14} />
            <span>СОЗДАТЬ БОТА</span>
          </button>
          <button 
            type="button" 
            className="btn btn-secondary btn-icon-only"
            onClick={fetchInstances}
            title="Обновить данные с сервера"
          >
            <IconRefresh size={14} />
          </button>
        </div>
      </div>

      {/* Панель фильтрации и поиска */}
      <div className="dashboard-filter-bar">
        <div className="search-input-wrapper">
          <IconSearch size={14} className="search-icon" />
          <input 
            type="text" 
            className="search-field"
            placeholder="Поиск по имени, ID, юзернейму или локации..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              type="button" 
              className="search-clear-btn"
              onClick={() => setSearchQuery('')}
            >
              ×
            </button>
          )}
        </div>

        <div className="filter-tabs">
          <button 
            type="button"
            className={`filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            ВСЕ ({instances.length})
          </button>
          <button 
            type="button"
            className={`filter-tab ${statusFilter === 'running' ? 'active' : ''}`}
            onClick={() => setStatusFilter('running')}
          >
            АКТИВНЫЕ ({runningCount})
          </button>
          <button 
            type="button"
            className={`filter-tab ${statusFilter === 'stopped' ? 'active' : ''}`}
            onClick={() => setStatusFilter('stopped')}
          >
            ОСТАНОВЛЕННЫЕ ({stoppedCount})
          </button>
        </div>
      </div>

      {aiErrorCount > 0 && (
        <div className="dashboard-global-ai-warning">
          <div className="ai-warning-icon-wrap">
            <IconAlert size={20} />
          </div>
          <div className="ai-warning-text-wrap">
            <strong>Внимание! У {aiErrorCount} {aiErrorCount === 1 ? 'бота' : 'ботов'} произошёл сбой генерации через AI.</strong>
            <span>Фолбэк-приветствие отключено — бот не отправляет ложных сообщений клиентам. Проверьте валидность API-ключа нейросети в настройках бота.</span>
          </div>
        </div>
      )}

      {error && (
        <div className="alert-box error">
          <IconAlert size={14} />
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="alert-close-btn">&times;</button>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>СИНХРОНИЗАЦИЯ СЕРВЕРА...</p>
        </div>
      ) : filteredInstances.length === 0 ? (
        <div className="empty-state-box">
          <div className="empty-icon-mark">
            <IconTerminal size={28} />
          </div>
          <h3>ИНСТАНСЫ НЕ НАЙДЕНЫ</h3>
          <p>
            {instances.length === 0 
              ? 'В системе нет зарегистрированных ботов. Создайте новую конфигурацию для запуска платформы.'
              : 'Нет ботов, соответствующих параметрам поискового запроса или выбранного фильтра.'}
          </p>
          {instances.length === 0 ? (
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={() => setShowCreateForm(true)}
            >
              <IconPlus size={14} />
              <span>СОЗДАТЬ ПЕРВОГО БОТА</span>
            </button>
          ) : (
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
            >
              СБРОСИТЬ ФИЛЬТРЫ
            </button>
          )}
        </div>
      ) : (
        <div className="instances-grid">
          {filteredInstances.map(inst => {
            const isExpanded = expandedInstanceId === inst.id;
            const isProcessing = actionLoading === inst.id;
            const cfg = inst.config || {};
            const age = cfg.age || 23;
            
            const presetLabel = 
              cfg.behaviorPreset === 'friendly' ? 'Дружелюбная' :
              cfg.behaviorPreset === 'business' ? 'Деловая' :
              cfg.behaviorPreset === 'mysterious' ? 'Загадочная' :
              cfg.behaviorPreset === 'custom' ? 'Пользовательский' : 'Кокетливая (Flirty)';

            const photosList = Array.isArray(cfg.photos) && cfg.photos.length > 0
              ? cfg.photos
              : (cfg.avatarUrl ? [cfg.avatarUrl] : []);

            return (
              <div key={inst.id} className={`instance-card status-${inst.status}`}>
                <div className="instance-card-header">
                  <div className="bot-profile-chip">
                    <div className="bot-avatar-frame">
                      {cfg.avatarUrl ? (
                        <img 
                          src={cfg.avatarUrl} 
                          alt={cfg.name || inst.id} 
                          onError={(e)=>{ (e.target as HTMLElement).style.display = 'none'; }} 
                        />
                      ) : (
                        <span className="bot-avatar-fallback">{(cfg.name || inst.id)[0].toUpperCase()}</span>
                      )}
                    </div>
                    <div className="bot-title-meta">
                      <div className="bot-name-row">
                        <h4>{cfg.name || 'БЕЗ ИМЕНИ'}</h4>
                        <span className="bot-age-tag">{age} ЛЕТ</span>
                        <span className="bot-id-tag">ID: {inst.id}</span>
                      </div>
                      <div className="bot-submeta">
                        <span className="bot-geo">{cfg.country || 'Локация не указана'}</span>
                        <span className="bot-separator">/</span>
                        <span className="bot-username">{cfg.tgProfile || '@нет_юзернейма'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="status-badge-wrap">
                    <span className={`status-badge ${inst.status}`}>
                      <span className="status-dot"></span>
                      <span className="status-text">
                        {inst.status === 'running' ? 'АКТИВЕН' : inst.status === 'error' ? 'ОШИБКА' : 'ОСТАНОВЛЕН'}
                      </span>
                    </span>
                  </div>
                </div>

                {inst.lastError && (
                  <div className="instance-ai-error-banner">
                    <div className="ai-error-banner-top">
                      <div className="ai-error-tag-group">
                        <IconAlert size={14} className="ai-error-alert-icon" />
                        <span className="ai-error-title">СБОЙ ГЕНЕРАЦИИ НЕЙРОСЕТИ</span>
                      </div>
                      {inst.lastErrorTime && (
                        <span className="ai-error-time font-mono">
                          {new Date(inst.lastErrorTime).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                      )}
                    </div>
                    <div className="ai-error-message font-mono">
                      {inst.lastError}
                    </div>
                    <div className="ai-error-resolution">
                      <span>Сообщение клиенту не отправлено (фолбэк выключен). Задайте ключ в настройках:</span>
                      <button 
                        type="button" 
                        className="ai-error-fix-btn"
                        onClick={() => setEditingInstance(inst)}
                      >
                        <IconEdit size={12} />
                        <span>Настроить ключ AI</span>
                      </button>
                    </div>
                  </div>
                )}

                <div className="instance-card-tags">
                  <span className="meta-tag" title="Количество загруженных фото">
                    <IconImage size={12} />
                    <span>{photosList.length} ФОТО</span>
                  </span>
                  <span className="meta-tag preset-tag" title="Стиль общения">
                    <span className="meta-tag-k">СТИЛЬ:</span>
                    <span>{presetLabel}</span>
                  </span>
                  <span className="meta-tag model-tag" title="Используемая нейросеть">
                    <IconCpu size={12} />
                    <span>{cfg.aiModel || 'gemini-2.5-flash'}</span>
                  </span>
                  <span className={`meta-tag ${cfg.aiApiKey ? 'api-key-set' : 'api-key-unset'}`} title={cfg.aiApiKey ? "API Ключ нейросети задан в боте" : "API Ключ нейросети не задан (используется переменная окружения)"}>
                    <span className="meta-tag-k">API:</span>
                    <span>{cfg.aiApiKey ? 'КЛЮЧ ЗАДАН' : 'ИЗ ОКРУЖЕНИЯ'}</span>
                  </span>
                  <span className="meta-tag delay-tag" title="Диапазон задержки ответов">
                    <span className="meta-tag-k">ТАЙМАУТ:</span>
                    <span>{cfg.minDelay || 2}–{cfg.maxDelay || 15}С</span>
                  </span>
                </div>

                {isExpanded && (
                  <div className="expanded-details-pane">
                    {photosList.length > 0 && (
                      <div className="detail-photos-preview">
                        <span className="detail-k">ФОТОАЛЬБОМ ({photosList.length}):</span>
                        <div className="mini-photos-strip">
                          {photosList.map((p, idx) => (
                            <div 
                              key={idx} 
                              className={`mini-photo-thumb ${p === cfg.avatarUrl ? 'is-main' : ''}`} 
                              title={p === cfg.avatarUrl ? 'Главное фото профиля' : `Фото #${idx+1}`}
                            >
                              <img src={p} alt={`Preview ${idx+1}`} onError={(e)=>{ (e.target as HTMLElement).style.display = 'none'; }} />
                              {p === cfg.avatarUrl && (
                                <span className="mini-star-mark">
                                  <IconStarFilled size={10} />
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="detail-grid">
                      <div className="detail-row">
                        <span className="detail-k">ИДЕНТИФИКАТОР:</span>
                        <span className="detail-v font-mono">{inst.id}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-k">TELEGRAM LINK:</span>
                        <span className="detail-v font-mono">{cfg.tlgLink || '—'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-k">ВЕБ-ССЫЛКА:</span>
                        <span className="detail-v">
                          {cfg.webLink ? (
                            <a href={cfg.webLink} target="_blank" rel="noreferrer" className="detail-link">{cfg.webLink}</a>
                          ) : '—'}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-k">МЕДИА-МАТЕРИАЛЫ:</span>
                        <span className="detail-v">
                          {cfg.webMedia ? (
                            <a href={cfg.webMedia} target="_blank" rel="noreferrer" className="detail-link">{cfg.webMedia}</a>
                          ) : '—'}
                        </span>
                      </div>
                    </div>

                    {(cfg.agentName || (cfg.variables && Object.keys(cfg.variables).length > 0)) && (
                      <div className="detail-variables-section">
                        <span className="detail-k">ПЕРЕМЕННЫЕ ШАБЛОНА:</span>
                        <div className="detail-variables-badges">
                          {cfg.agentName && (
                            <span className="detail-var-tag">
                              <strong>agent_name:</strong> {cfg.agentName}
                            </span>
                          )}
                          {cfg.variables && Object.entries(cfg.variables).map(([k, v]) => (
                            <span key={k} className="detail-var-tag">
                              <strong>{k}:</strong> {v}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {cfg.systemPrompt && (
                      <div className="detail-prompt">
                        <span className="detail-k">СИСТЕМНЫЙ ПРОМПТ (CUSTOM PROMPT):</span>
                        <pre className="prompt-pre">{cfg.systemPrompt}</pre>
                      </div>
                    )}
                  </div>
                )}

                <div className="instance-card-footer">
                  <button 
                    type="button" 
                    className="btn btn-expand" 
                    onClick={() => toggleExpand(inst.id)}
                  >
                    <span>{isExpanded ? 'СВЕРНУТЬ' : 'ПАРАМЕТРЫ'}</span>
                    {isExpanded ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
                  </button>

                  <div className="instance-btn-group">
                    {inst.status === 'running' ? (
                      <button 
                        type="button" 
                        className="btn btn-warning btn-icon-only"
                        disabled={isProcessing}
                        onClick={() => handleStop(inst.id)}
                        title={isProcessing ? 'Остановка...' : 'Остановить бота'}
                      >
                        <IconStop size={14} />
                      </button>
                    ) : (
                      <button 
                        type="button" 
                        className="btn btn-success btn-icon-only"
                        disabled={isProcessing}
                        onClick={() => handleStart(inst.id)}
                        title={isProcessing ? 'Запуск...' : 'Запустить бота'}
                      >
                        <IconPlay size={14} />
                      </button>
                    )}

                    <button 
                      type="button" 
                      className={`btn btn-icon-only ${inst.status === 'running' ? 'btn-secondary' : 'btn-outline'}`}
                      onClick={() => setScreenshotInstance(inst)}
                      title="Посмотреть экран браузера (скриншот)"
                    >
                      <IconCamera size={15} />
                    </button>

                    <button 
                      type="button" 
                      className="btn btn-outline btn-icon-only"
                      onClick={() => setEditingInstance(inst)}
                      title="Редактировать параметры бота"
                    >
                      <IconEdit size={14} />
                    </button>

                    <button 
                      type="button" 
                      className="btn btn-danger btn-icon-only"
                      onClick={() => handleDelete(inst.id, cfg.name || inst.id)}
                      title="Удалить бота"
                    >
                      <IconTrash size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Модальное окно создания */}
      {showCreateForm && (
        <CreateInstanceForm 
          onSubmit={handleCreateInstance}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* Модальное окно редактирования */}
      {editingInstance && (
        <EditInstanceForm 
          instance={editingInstance}
          onSubmit={handleUpdate}
          onCancel={() => setEditingInstance(null)}
        />
      )}

      {/* Модальное окно скриншота */}
      {screenshotInstance && (
        <ScreenshotModal 
          instanceId={screenshotInstance.id}
          instanceName={screenshotInstance.config?.name || screenshotInstance.id}
          isRunning={screenshotInstance.status === 'running'}
          onClose={() => setScreenshotInstance(null)}
        />
      )}
    </div>
  );
};